/**
 * Stack Configuration API Service
 *
 * API service for managing client technology stack and standards configuration
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type APIResponse } from './utils';
import { API_ENDPOINTS } from './endpoints';
import type {
  StackConfiguration,
  StackConfigurationRequest,
  StackConfigurationResponse
} from '../../types/stackConfiguration';

// Stack configuration endpoints are in API_ENDPOINTS.CLIENTS

/**
 * Save stack configuration for a client
 */
export async function saveStackConfiguration(
  clientId: string,
  configuration: Omit<StackConfigurationRequest, 'Client_ID'>
): Promise<APIResponse<StackConfiguration>> {
  try {
    const requestBody: StackConfigurationRequest = {
      Client_ID: clientId,
      Frontend_Stack: configuration.Frontend_Stack,
      Backend_Stack: configuration.Backend_Stack,
      Cloud_Provider: configuration.Cloud_Provider,
      Database_Technology: configuration.Database_Technology,
      Architecture_Type: configuration.Architecture_Type,
      Selected_Standards: configuration.Selected_Standards,
      Additional_Technologies: configuration.Additional_Technologies,
      Custom_Standards: configuration.Custom_Standards
    };

    const response = await Make_Authorized_API_Call<StackConfiguration>(
      API_ENDPOINTS.CLIENTS.STACK_CONFIG_CREATE,
      'POST',
      requestBody
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: 'Stack configuration saved successfully'
      };
    }

    return {
      success: false,
      data: {} as StackConfiguration,
      message: response.message || 'Failed to save stack configuration'
    };
  } catch (error) {
    console.error('Error saving stack configuration:', error);
    return {
      success: false,
      data: {} as StackConfiguration,
      message: 'An error occurred while saving stack configuration'
    };
  }
}

/**
 * Get stack configuration for a client
 */
export async function getStackConfiguration(
  clientId: string
): Promise<APIResponse<StackConfiguration>> {
  try {
    const response = await Make_Authorized_API_Call<StackConfiguration>(
      API_ENDPOINTS.CLIENTS.STACK_CONFIG_GET,
      'GET',
      undefined,
      { params: { Client_ID: clientId } }
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: 'Stack configuration loaded successfully'
      };
    }

    return {
      success: false,
      data: {} as StackConfiguration,
      message: response.message || 'Failed to load stack configuration'
    };
  } catch (error) {
    console.error('Error loading stack configuration:', error);
    return {
      success: false,
      data: {} as StackConfiguration,
      message: 'An error occurred while loading stack configuration'
    };
  }
}

/**
 * Update stack configuration for a client
 */
export async function updateStackConfiguration(
  clientId: string,
  configuration: Partial<Omit<StackConfigurationRequest, 'Client_ID'>>
): Promise<APIResponse<StackConfiguration>> {
  try {
    // Note: No separate update endpoint - using create which handles upsert
    const response = await Make_Authorized_API_Call<StackConfiguration>(
      API_ENDPOINTS.CLIENTS.STACK_CONFIG_CREATE,
      'POST',
      { Client_ID: clientId, ...configuration }
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: 'Stack configuration updated successfully'
      };
    }

    return {
      success: false,
      data: {} as StackConfiguration,
      message: response.message || 'Failed to update stack configuration'
    };
  } catch (error) {
    console.error('Error updating stack configuration:', error);
    return {
      success: false,
      data: {} as StackConfiguration,
      message: 'An error occurred while updating stack configuration'
    };
  }
}

/**
 * Delete stack configuration for a client
 */
export async function deleteStackConfiguration(
  clientId: string
): Promise<APIResponse<{ success: boolean }>> {
  // No delete endpoint in backend - fail loud
  throw new Error('Stack configuration delete not implemented in backend');
}
